import { apiRequest } from "../lib/apiClient";
import type { AcceptInviteRequest, AcceptInviteResponse, UserCreate, UserInviteOut, UserManagementOut, UserUpdate } from "./types";

/** `GET /users` — `OWNER` sees every user in the tenant, `BRANCH_MANAGER` sees only
 * users assigned to their own branch (backend-enforced, `app/services/user_service.py`). */
export async function listUsers(accessToken: string): Promise<UserManagementOut[]> {
  return apiRequest<UserManagementOut[]>("/users", { accessToken });
}

/** `POST /users` — `OWNER`-only. Adds a new staff account directly (no email
 * infrastructure exists, so the response's `setup_token` is the login link the owner
 * hands to the new hire themselves — Slack, WhatsApp, in person). Returned exactly
 * once and cannot be retrieved again. */
export async function createUser(accessToken: string, body: UserCreate): Promise<UserInviteOut> {
  return apiRequest<UserInviteOut>("/users", { method: "POST", accessToken, body });
}

/** `PATCH /users/{id}` — `OWNER`-only. Reassign role/branch and/or suspend
 * (`is_active: false`) / reactivate (`is_active: true`). Never removes a user — see
 * `deleteUser` below for that. */
export async function updateUser(accessToken: string, userId: string, body: UserUpdate): Promise<UserManagementOut> {
  return apiRequest<UserManagementOut>(`/users/${userId}`, { method: "PATCH", accessToken, body });
}

/** `DELETE /users/{id}` — `OWNER`-only. Real (soft) removal of a staff account: the
 * account stops working and disappears from the staff list, but its historical
 * sales/stock-log attribution is preserved (`app/services/user_service.py::delete_user`).
 * Refuses to delete your own account (400). */
export async function deleteUser(accessToken: string, userId: string): Promise<void> {
  await apiRequest<void>(`/users/${userId}`, { method: "DELETE", accessToken });
}

/** `POST /auth/accept-invite` — public, no auth. Consumes a `setup_token` from
 * `POST /users` (or `POST /platform/tenants`) and sets the real password. */
export async function acceptInvite(body: AcceptInviteRequest): Promise<AcceptInviteResponse> {
  return apiRequest<AcceptInviteResponse>("/auth/accept-invite", { method: "POST", body });
}
